import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { AlertCircle, Upload, FileText, Building, ArrowLeft, X } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
import { toast } from 'sonner';

const RegistrationPage = ({ onRegistrationSuccess }) => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    // Login credentials
    email: '',
    password: '',
    confirmPassword: '',
    
    // Pharmacy Information
    pharmacy_name: '',
    pharmacy_license_number: '',
    primary_contact_name: '',
    phone: '',
    
    // Business Address
    street_address: '',
    city: '',
    province: 'Alberta',
    postal_code: '',
    
    // Business Details
    years_in_operation: '',
    practice_type: '',
    
    // PAD Agreement (MANDATORY)
    pad_agreement_signed: false,
    bank_account_holder: '',
    bank_institution_number: '',
    bank_transit_number: '',
    bank_account_number: ''
  });
  
  const [documents, setDocuments] = useState({
    pharmacy_license: null,
    business_registration: null,
    void_cheque: null
  });
  
  const [errors, setErrors] = useState({});

  const validateStep = (step) => {
    const newErrors = {};
    
    if (step === 1) {
      if (!formData.email) newErrors.email = 'Email is required';
      if (!formData.password) newErrors.password = 'Password is required';
      if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      if (!formData.pharmacy_name) newErrors.pharmacy_name = 'Pharmacy name is required';
      if (!formData.pharmacy_license_number) newErrors.pharmacy_license_number = 'License number is required';
      if (!formData.primary_contact_name) newErrors.primary_contact_name = 'Primary contact name is required';
      if (!formData.phone) newErrors.phone = 'Phone number is required';
    }
    
    if (step === 2) {
      if (!formData.street_address) newErrors.street_address = 'Street address is required';
      if (!formData.city) newErrors.city = 'City is required';
      if (!formData.postal_code) newErrors.postal_code = 'Postal code is required';
      if (!formData.practice_type) newErrors.practice_type = 'Practice type is required';
    }
    
    if (step === 3) {
      // PAD is MANDATORY
      if (!formData.pad_agreement_signed) {
        newErrors.pad_agreement_signed = 'You must accept the PAD agreement to register';
      }
      if (!formData.bank_account_holder) newErrors.bank_account_holder = 'Account holder name is required';
      if (!formData.bank_institution_number) newErrors.bank_institution_number = 'Institution number is required (3 digits)';
      if (formData.bank_institution_number && formData.bank_institution_number.length !== 3) {
        newErrors.bank_institution_number = 'Institution number must be exactly 3 digits';
      }
      if (!formData.bank_transit_number) newErrors.bank_transit_number = 'Transit number is required (5 digits)';
      if (formData.bank_transit_number && formData.bank_transit_number.length !== 5) {
        newErrors.bank_transit_number = 'Transit number must be exactly 5 digits';
      }
      if (!formData.bank_account_number) newErrors.bank_account_number = 'Account number is required';
      if (formData.bank_account_number && formData.bank_account_number.length < 7) {
        newErrors.bank_account_number = 'Account number must be at least 7 digits';
      }
    }
    
    // Step 4 is documents - all optional
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined
      }));
    }
  };

  const handleFileUpload = (documentType, file) => {
    setDocuments(prev => ({
      ...prev,
      [documentType]: file
    }));
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(currentStep)) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      // Submit registration
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      const response = await fetch(`${backendUrl}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Registration failed');
      }
      
      // Registration successful, now auto-login
      const loginResponse = await fetch(`${backendUrl}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password
        })
      });
      
      if (loginResponse.ok) {
        const loginData = await loginResponse.json();
        
        // Store auth token AND user data
        localStorage.setItem('auth_token', loginData.access_token);
        localStorage.setItem('user_data', JSON.stringify(loginData.user));
        
        // Upload documents if provided
        for (const [docType, file] of Object.entries(documents)) {
          if (file) {
            const docFormData = new FormData();
            docFormData.append('file', file);
            
            await fetch(`${backendUrl}/api/auth/upload-document/${docType}`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${loginData.access_token}`
              },
              body: docFormData
            });
          }
        }
        
        toast.success('Registration successful! Welcome to Alberta Pharmacy Supply. You can now place orders on account.');
        
        // Redirect to homepage after 1.5 seconds
        setTimeout(() => {
          window.location.href = '/';  // Force full page reload to trigger App.js useEffect
        }, 1500);
      } else {
        throw new Error('Auto-login failed after registration');
      }
      
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <Building className="w-12 h-12 mx-auto mb-4 text-purple-600" />
        <h2 className="text-2xl font-bold text-slate-900">Pharmacy Information</h2>
        <p className="text-slate-600">Tell us about your pharmacy and contact details</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <Label htmlFor="email">Email Address *</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={errors.email ? 'border-red-500' : ''}
          />
          {errors.email && <p className="text-red-500 text-sm">{errors.email}</p>}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number *</Label>
          <Input
            id="phone"
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            className={errors.phone ? 'border-red-500' : ''}
          />
          {errors.phone && <p className="text-red-500 text-sm">{errors.phone}</p>}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="password">Password *</Label>
          <Input
            id="password"
            type="password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            className={errors.password ? 'border-red-500' : ''}
          />
          {errors.password && <p className="text-red-500 text-sm">{errors.password}</p>}
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm Password *</Label>
          <Input
            id="confirmPassword"
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            className={errors.confirmPassword ? 'border-red-500' : ''}
          />
          {errors.confirmPassword && <p className="text-red-500 text-sm">{errors.confirmPassword}</p>}
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="pharmacy_name">Pharmacy Name *</Label>
          <Input
            id="pharmacy_name"
            value={formData.pharmacy_name}
            onChange={(e) => handleInputChange('pharmacy_name', e.target.value)}
            className={errors.pharmacy_name ? 'border-red-500' : ''}
            placeholder="Your Pharmacy Name"
          />
          {errors.pharmacy_name && <p className="text-red-500 text-sm">{errors.pharmacy_name}</p>}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="pharmacy_license_number">Pharmacy License Number *</Label>
            <Input
              id="pharmacy_license_number"
              value={formData.pharmacy_license_number}
              onChange={(e) => handleInputChange('pharmacy_license_number', e.target.value)}
              className={errors.pharmacy_license_number ? 'border-red-500' : ''}
              placeholder="AB12345"
            />
            {errors.pharmacy_license_number && <p className="text-red-500 text-sm">{errors.pharmacy_license_number}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="primary_contact_name">Primary Contact Name *</Label>
            <Input
              id="primary_contact_name"
              value={formData.primary_contact_name}
              onChange={(e) => handleInputChange('primary_contact_name', e.target.value)}
              className={errors.primary_contact_name ? 'border-red-500' : ''}
              placeholder="John Smith"
            />
            {errors.primary_contact_name && <p className="text-red-500 text-sm">{errors.primary_contact_name}</p>}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <Building className="w-12 h-12 mx-auto mb-4 text-purple-600" />
        <h2 className="text-2xl font-bold text-slate-900">Business Details</h2>
        <p className="text-slate-600">Your pharmacy address and business information</p>
      </div>
      
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="street_address">Street Address *</Label>
          <Input
            id="street_address"
            value={formData.street_address}
            onChange={(e) => handleInputChange('street_address', e.target.value)}
            className={errors.street_address ? 'border-red-500' : ''}
            placeholder="123 Main Street"
          />
          {errors.street_address && <p className="text-red-500 text-sm">{errors.street_address}</p>}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="city">City *</Label>
            <Input
              id="city"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              className={errors.city ? 'border-red-500' : ''}
              placeholder="Calgary"
            />
            {errors.city && <p className="text-red-500 text-sm">{errors.city}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="province">Province</Label>
            <Select value={formData.province} onValueChange={(value) => handleInputChange('province', value)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Alberta">Alberta</SelectItem>
                <SelectItem value="British Columbia">British Columbia</SelectItem>
                <SelectItem value="Saskatchewan">Saskatchewan</SelectItem>
                <SelectItem value="Manitoba">Manitoba</SelectItem>
                <SelectItem value="Ontario">Ontario</SelectItem>
                <SelectItem value="Quebec">Quebec</SelectItem>
                <SelectItem value="New Brunswick">New Brunswick</SelectItem>
                <SelectItem value="Nova Scotia">Nova Scotia</SelectItem>
                <SelectItem value="Prince Edward Island">Prince Edward Island</SelectItem>
                <SelectItem value="Newfoundland and Labrador">Newfoundland and Labrador</SelectItem>
                <SelectItem value="Northwest Territories">Northwest Territories</SelectItem>
                <SelectItem value="Nunavut">Nunavut</SelectItem>
                <SelectItem value="Yukon">Yukon</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="postal_code">Postal Code *</Label>
            <Input
              id="postal_code"
              value={formData.postal_code}
              onChange={(e) => handleInputChange('postal_code', e.target.value)}
              className={errors.postal_code ? 'border-red-500' : ''}
              placeholder="T2P 1J9"
            />
            {errors.postal_code && <p className="text-red-500 text-sm">{errors.postal_code}</p>}
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="practice_type">Practice Type *</Label>
            <Select value={formData.practice_type} onValueChange={(value) => handleInputChange('practice_type', value)}>
              <SelectTrigger className={errors.practice_type ? 'border-red-500' : ''}>
                <SelectValue placeholder="Select practice type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Community Pharmacy">Community Pharmacy</SelectItem>
                <SelectItem value="Hospital Pharmacy">Hospital Pharmacy</SelectItem>
                <SelectItem value="Clinical Pharmacy">Clinical Pharmacy</SelectItem>
                <SelectItem value="Long-term Care Pharmacy">Long-term Care Pharmacy</SelectItem>
                <SelectItem value="Compounding Pharmacy">Compounding Pharmacy</SelectItem>
                <SelectItem value="Veterinary Pharmacy">Veterinary Pharmacy</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
            {errors.practice_type && <p className="text-red-500 text-sm">{errors.practice_type}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="years_in_operation">Years in Operation (Optional)</Label>
            <Input
              id="years_in_operation"
              type="number"
              min="0"
              value={formData.years_in_operation}
              onChange={(e) => handleInputChange('years_in_operation', parseInt(e.target.value) || '')}
              placeholder="10"
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Building className="w-8 h-8 text-purple-600" />
        </div>
        <h2 className="text-2xl font-bold text-slate-900">PAD Billing Agreement</h2>
        <p className="text-slate-600">Required for all Alberta Pharmacy Supply accounts</p>
      </div>
      
      <Alert className="bg-purple-50 border-purple-200">
        <AlertCircle className="h-4 w-4 text-purple-600" />
        <AlertDescription>
          <strong className="text-purple-900">Why PAD Billing?</strong>
          <ul className="mt-2 space-y-1 text-sm text-purple-800">
            <li>• Order on account with $2,500 credit limit</li>
            <li>• Invoices generated every 2 weeks (1st and 15th)</li>
            <li>• Automatic payment via pre-authorized debit</li>
            <li>• No need to pay at checkout - seamless ordering</li>
            <li>• Detailed invoice for all orders in the period</li>
          </ul>
        </AlertDescription>
      </Alert>

      <div className="space-y-5 p-5 bg-slate-50 rounded-lg border-2 border-slate-200">
        <div className="flex items-start space-x-3">
          <Checkbox 
            id="pad_agreement"
            checked={formData.pad_agreement_signed}
            onCheckedChange={(checked) => handleInputChange('pad_agreement_signed', checked)}
            className="mt-1"
          />
          <Label htmlFor="pad_agreement" className="text-sm leading-relaxed cursor-pointer">
            <strong>I agree to Pre-Authorized Debit (PAD) terms:</strong> I authorize Alberta Pharmacy Supply to withdraw funds from my business bank account on the 1st and 15th of each month for all orders placed during the billing period. I understand my credit limit is $2,500 per billing period. *
          </Label>
        </div>
        {errors.pad_agreement_signed && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{errors.pad_agreement_signed}</AlertDescription>
          </Alert>
        )}
      </div>

      <div className="space-y-4 p-5 bg-white rounded-lg border-2 border-slate-200">
        <h3 className="font-semibold text-slate-900 text-lg">Banking Information</h3>
        <p className="text-sm text-slate-600 mb-4">
          Provide your business bank account details for automatic PAD withdrawals.
        </p>
        
        <div className="space-y-2">
          <Label htmlFor="bank_account_holder">Account Holder Name *</Label>
          <Input
            id="bank_account_holder"
            value={formData.bank_account_holder}
            onChange={(e) => handleInputChange('bank_account_holder', e.target.value)}
            className={errors.bank_account_holder ? 'border-red-500' : ''}
            placeholder="Your Pharmacy Name or Legal Business Name"
          />
          {errors.bank_account_holder && <p className="text-red-500 text-sm">{errors.bank_account_holder}</p>}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="bank_institution_number">Institution Number *</Label>
            <Input
              id="bank_institution_number"
              value={formData.bank_institution_number}
              onChange={(e) => handleInputChange('bank_institution_number', e.target.value.replace(/\D/g, '').slice(0, 3))}
              className={errors.bank_institution_number ? 'border-red-500' : ''}
              placeholder="001"
              maxLength={3}
            />
            <p className="text-xs text-slate-500">3 digits (e.g., 001 for BMO)</p>
            {errors.bank_institution_number && <p className="text-red-500 text-sm">{errors.bank_institution_number}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="bank_transit_number">Transit Number *</Label>
            <Input
              id="bank_transit_number"
              value={formData.bank_transit_number}
              onChange={(e) => handleInputChange('bank_transit_number', e.target.value.replace(/\D/g, '').slice(0, 5))}
              className={errors.bank_transit_number ? 'border-red-500' : ''}
              placeholder="12345"
              maxLength={5}
            />
            <p className="text-xs text-slate-500">5 digits</p>
            {errors.bank_transit_number && <p className="text-red-500 text-sm">{errors.bank_transit_number}</p>}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="bank_account_number">Account Number *</Label>
            <Input
              id="bank_account_number"
              value={formData.bank_account_number}
              onChange={(e) => handleInputChange('bank_account_number', e.target.value.replace(/\D/g, '').slice(0, 12))}
              className={errors.bank_account_number ? 'border-red-500' : ''}
              placeholder="1234567"
              maxLength={12}
            />
            <p className="text-xs text-slate-500">7-12 digits</p>
            {errors.bank_account_number && <p className="text-red-500 text-sm">{errors.bank_account_number}</p>}
          </div>
        </div>
        
        <Alert className="bg-green-50 border-green-200 mt-4">
          <AlertCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-800 text-sm">
            <strong>Your banking information is secure.</strong> We use industry-standard encryption and only use this information for authorized PAD withdrawals on billing dates (1st and 15th of each month).
          </AlertDescription>
        </Alert>
      </div>

      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">📅 Billing Schedule</h4>
        <p className="text-sm text-blue-800">
          <strong>Cycle 1:</strong> Orders placed from 1st-15th → Invoiced & withdrawn on 15th<br/>
          <strong>Cycle 2:</strong> Orders placed from 16th-end of month → Invoiced & withdrawn on 1st of next month
        </p>
      </div>
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <FileText className="w-12 h-12 mx-auto mb-4 text-blue-600" />
        <h2 className="text-2xl font-bold text-slate-900">Document Upload</h2>
        <p className="text-slate-600">Upload required documents (optional - you can upload these later)</p>
      </div>
      
      <div className="space-y-4">
        <DocumentUpload 
          title="Pharmacy License"
          description="Upload your current pharmacy license"
          documentType="pharmacy_license"
          file={documents.pharmacy_license}
          onChange={(file) => handleFileUpload('pharmacy_license', file)}
        />
        
        <DocumentUpload 
          title="Business Registration"
          description="Business registration or incorporation documents"
          documentType="business_registration"
          file={documents.business_registration}
          onChange={(file) => handleFileUpload('business_registration', file)}
        />
        
        <DocumentUpload 
          title="Void Cheque"
          description="Void cheque for banking verification"
          documentType="void_cheque"
          file={documents.void_cheque}
          onChange={(file) => handleFileUpload('void_cheque', file)}
        />
      </div>
      
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          You can skip document uploads now and add them later from your dashboard. However, uploading them now helps us verify your account faster.
        </AlertDescription>
      </Alert>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back to Store Button */}
        <div className="mb-6">
          <Button 
            variant="outline" 
            onClick={() => navigate('/')}
            className="flex items-center"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Store
          </Button>
        </div>
        
        <div className="text-center mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_meds-storage/artifacts/16xboc6r_WhatsApp%20Image%202025-10-14%20at%2021.25.24_e01b08d0.jpg"
            alt="Alberta Pharmacy Supply Logo"
            className="w-20 h-20 mx-auto mb-4"
          />
          <h1 className="text-3xl font-bold text-slate-900">Join Alberta Pharmacy Supplies</h1>
          <p className="text-slate-600 mt-2">Create your professional account for seamless ordering</p>
        </div>
        
        {/* Step Indicator */}
        <div className="flex justify-center mb-8">
          <div className="flex space-x-4">
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
                  currentStep >= step 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-gray-200 text-gray-500'
                }`}
              >
                {step}
              </div>
            ))}
          </div>
        </div>
        
        <Card>
          <CardContent className="p-8">
            {currentStep === 1 && renderStep1()}
            {currentStep === 2 && renderStep2()}
            {currentStep === 3 && renderStep3()}
            {currentStep === 4 && renderStep4()}
            
            <div className="flex justify-between mt-8 pt-6 border-t">
              {currentStep > 1 && (
                <Button variant="outline" onClick={handleBack} disabled={isLoading}>
                  Back
                </Button>
              )}
              
              <div className="ml-auto">
                {currentStep < 4 ? (
                  <Button onClick={handleNext} disabled={isLoading}>
                    Next
                  </Button>
                ) : (
                  <Button onClick={handleSubmit} disabled={isLoading}>
                    {isLoading ? 'Creating Account...' : 'Create Account'}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Document Upload Component
const DocumentUpload = ({ title, description, documentType, file, onChange }) => {
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    onChange(selectedFile);
  };

  return (
    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-gray-400 transition-colors">
      <div className="flex items-center space-x-4">
        <div className="flex-shrink-0">
          <Upload className="w-8 h-8 text-gray-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-sm font-medium text-gray-900">{title}</h3>
          <p className="text-sm text-gray-500">{description}</p>
          {file && (
            <p className="text-sm text-green-600 mt-1">
              ✓ {file.name}
            </p>
          )}
        </div>
        <div className="flex-shrink-0">
          <label className="cursor-pointer">
            <input
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png,.gif"
              onChange={handleFileChange}
            />
            <Button variant="outline" size="sm" type="button">
              {file ? 'Change' : 'Upload'}
            </Button>
          </label>
        </div>
      </div>
    </div>
  );
};

export default RegistrationPage;